import jsPDF from 'jspdf'
import 'jspdf-autotable'

// Helper function to convert image URL to base64
const imageToBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.width,
        height: img.height
      })
    }
    img.onerror = (error) => reject(error)
    img.src = url
  })
}


// Helper function to format currency
const formatCurrency = (amount) => {
  return Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Generate a professional PDF invoice that matches the InvoiceView design
export const generateInvoicePDF = async (invoice, client, settings) => {
  const doc = new jsPDF()

  // Modern Color Palette
  const accentColor = [15, 23, 42] // Slate-900 (Almost Black)
  const primaryColor = [37, 99, 235] // Blue-600
  const textColor = [15, 23, 42] // Slate-900
  const grayColor = [100, 116, 139] // Slate-500
  const lightGrayBg = [248, 250, 252] // Slate-50
  const separatorColor = [226, 232, 240] // Slate-200

  const margin = 20
  const pageWidth = doc.internal.pageSize.getWidth()
  const contentWidth = pageWidth - (margin * 2)

  let yPos = 25

  // Header Section - Clean design with logo
  let logoData = null
  try {
    if (settings.logo) {
      logoData = await imageToBase64(settings.logo)
    } else {
      logoData = await imageToBase64('/ncinq-favicon.png')
    }
  } catch (e) {
    console.log('Logo not found, using fallback icon')
  }

  if (logoData && logoData.dataUrl) {
    const maxWidth = 50
    const maxHeight = 25

    let logoWidth = logoData.width
    let logoHeight = logoData.height

    const ratio = Math.min(maxWidth / logoWidth, maxHeight / logoHeight)
    logoWidth *= ratio
    logoHeight *= ratio

    doc.addImage(logoData.dataUrl, 'PNG', margin, yPos - 5, logoWidth, logoHeight)
  } else {
    // Fallback Icon Minimal
    doc.setFillColor(...accentColor)
    doc.circle(margin + 6, yPos + 4, 6, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text('5', margin + 6, yPos + 7.5, { align: 'center' })
  }

  // "INVOICE" Title
  doc.setTextColor(...accentColor)
  doc.setFontSize(32)
  doc.setFont('helvetica', 'bold')
  // Add some tracking by using upper case and a light font if possible, but jsPDF only has basic helvetica
  doc.text('INVOICE', pageWidth - margin, yPos + 12, { align: 'right' })

  yPos += 30

  // Subtle separator line under header
  doc.setDrawColor(...separatorColor)
  doc.setLineWidth(0.5)
  doc.line(margin, yPos, pageWidth - margin, yPos)
  
  yPos += 15

  // === Flex-like minimal Layout ===
  
  // Left side: Company & Bill To
  // Right side: Invoice Meta

  // Company Information
  doc.setTextColor(...accentColor)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.text(settings.businessName || 'Your Company', margin, yPos)

  yPos += 5
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...grayColor)

  const businessAddress = settings.businessAddress || ''
  if (businessAddress) {
    const addressLines = businessAddress.split('\n')
    addressLines.forEach((line) => {
      doc.text(line, margin, yPos)
      yPos += 5
    })
  }

  if (settings.businessEmail) {
    doc.text(settings.businessEmail, margin, yPos)
    yPos += 15
  } else {
    yPos += 10
  }

  const splitYPos = yPos

  // Bill To (Left side)
  doc.setTextColor(...grayColor)
  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.text('BILL TO', margin, yPos)
  
  let billToY = yPos + 6

  doc.setTextColor(...textColor)
  doc.setFontSize(10)
  if (client?.businessName) {
    doc.text(client.businessName, margin, billToY)
    billToY += 5
    doc.setFont('helvetica', 'normal')
    doc.text(client?.name || '', margin, billToY)
    billToY += 5
  } else {
    doc.text(client?.name || 'Client Name', margin, billToY)
    billToY += 5
  }

  if (client?.address) {
    const clientAddressLines = client.address.split('\n')
    clientAddressLines.forEach(line => {
      doc.text(line, margin, billToY)
      billToY += 5
    })
  }
  doc.setTextColor(...grayColor)
  doc.setFontSize(9)
  doc.text(client?.email || '', margin, billToY)

  // Invoice Details (Right side grid)
  let detailsY = splitYPos
  const labelX = pageWidth - margin - 70
  const valueX = pageWidth - margin

  const printDetailRow = (label, value, isBoldValue = false, highlightColor = null) => {
    doc.setTextColor(...grayColor)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text(label.toUpperCase(), labelX, detailsY)
    
    if (highlightColor) {
      doc.setTextColor(...highlightColor)
    } else {
      doc.setTextColor(...textColor)
    }
    
    doc.setFontSize(10)
    doc.setFont('helvetica', isBoldValue ? 'bold' : 'normal')
    doc.text(value, valueX, detailsY, { align: 'right' })
    detailsY += 8
  }

  printDetailRow('Invoice Number', invoice.invoiceNumber || '-', true)
  if (invoice.subject) {
    printDetailRow('Subject', invoice.subject)
  }
  printDetailRow('Issue Date', formatDate(invoice.issueDate))
  printDetailRow('Due Date', formatDate(invoice.dueDate))

  // Status Indicator logic inside the details grid
  detailsY += 2
  doc.setTextColor(...grayColor)
  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.text('STATUS', labelX, detailsY + 3)

  const statusColors = {
    draft: [100, 116, 139],
    sent: [37, 99, 235],
    viewed: [109, 40, 217], // Modern violet
    partial: [217, 119, 6], // Amber
    paid: [22, 163, 74],    // Green
    overdue: [220, 38, 38], // Red
    void: [100, 116, 139]
  }

  const statusColor = statusColors[invoice.status] || statusColors.draft
  const statusLabel = invoice.status.toUpperCase()
  
  doc.setFontSize(8)
  doc.setTextColor(...statusColor)
  doc.text(statusLabel, valueX, detailsY + 3, { align: 'right' })

  // Add subtle dot indicator
  const textWidth = doc.getTextWidth(statusLabel)
  doc.setFillColor(...statusColor)
  doc.circle(valueX - textWidth - 3, detailsY + 2, 1.5, 'F')

  yPos = Math.max(billToY, detailsY) + 20

  // Items Table (Minimalist)
  const tableHeaders = [['DESCRIPTION', 'QTY', 'RATE', 'AMOUNT']]
  const tableData = invoice.items.map(item => [
    item.description,
    item.quantity.toString(),
    `$${formatCurrency(item.rate)}`,
    `$${formatCurrency(item.quantity * item.rate)}`
  ])

  doc.autoTable({
    startY: yPos,
    head: tableHeaders,
    body: tableData,
    theme: 'plain',
    styles: {
      fontSize: 9.5,
      cellPadding: { top: 6, right: 0, bottom: 6, left: 0 },
      textColor: textColor,
      font: 'helvetica'
    },
    headStyles: {
      fillColor: false,
      textColor: grayColor,
      fontStyle: 'bold',
      fontSize: 8,
      lineWidth: { bottom: 0.5 },
      lineColor: separatorColor
    },
    bodyStyles: {
      lineWidth: { bottom: 0.5 },
      lineColor: [241, 245, 249] // Very subtle Slate-100 between rows
    },
    columnStyles: {
      0: { cellWidth: 'auto', halign: 'left' },
      1: { cellWidth: 25, halign: 'center' },
      2: { cellWidth: 35, halign: 'right' },
      3: { cellWidth: 40, halign: 'right' }
    },
    margin: { left: margin, right: margin }
  })

  yPos = doc.lastAutoTable.finalY + 15

  // Totals Section - Aligned right
  const totalsWidth = 80
  const totalsX = pageWidth - margin - totalsWidth
  const totalsValueX = pageWidth - margin

  const printSummaryRow = (label, value, isHighlight = false, valueColor = textColor) => {
    doc.setFontSize(10)
    doc.setTextColor(...grayColor)
    doc.setFont('helvetica', isHighlight ? 'bold' : 'normal')
    doc.text(label, totalsX, yPos)
    
    doc.setTextColor(...valueColor)
    doc.text(value, totalsValueX, yPos, { align: 'right' })
    yPos += 8
  }

  printSummaryRow('Subtotal', `$${formatCurrency(invoice.subtotal)}`)

  if (invoice.taxRate > 0) {
    printSummaryRow(`${invoice.taxLabel} (${invoice.taxRate}%)`, `$${formatCurrency(invoice.taxAmount)}`)
  }

  if (invoice.discount > 0) {
    printSummaryRow('Discount', `-$${formatCurrency(invoice.discount)}`, false, [22, 163, 74])
  }

  yPos += 2 // Extra spacing before total

  doc.setDrawColor(...separatorColor)
  doc.setLineWidth(0.5)
  doc.line(totalsX, yPos - 5, pageWidth - margin, yPos - 5)

  // Total
  doc.setTextColor(...accentColor)
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('Total Due', totalsX, yPos + 2)
  
  doc.setTextColor(...primaryColor)
  doc.setFontSize(16)
  doc.text(`$${formatCurrency(invoice.total)}`, totalsValueX, yPos + 2, { align: 'right' })

  yPos += 12

  // Currency Exchange Equivalent
  if (settings.enableExchange && settings.exchangeRate > 0) {
    const convertedTotal = invoice.total * settings.exchangeRate
    const formattedConverted = convertedTotal.toLocaleString('en-US', {
      style: 'currency',
      currency: settings.exchangeCurrency || 'GYD'
    })
    
    doc.setTextColor(...grayColor)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text(`Equivalent in ${settings.exchangeCurrency || 'GYD'}`, totalsX, yPos)
    doc.text(formattedConverted, totalsValueX, yPos, { align: 'right' })
    yPos += 6
  }

  // Partial payment indicator
  if (invoice.partialAmount) {
    yPos += 4
    printSummaryRow('Amount Paid', `-$${formatCurrency(invoice.partialAmount)}`, false, [22, 163, 74])
    
    yPos += 2
    doc.setTextColor(...accentColor)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('Balance Due', totalsX, yPos)
    doc.text(`$${formatCurrency(invoice.total - invoice.partialAmount)}`, totalsValueX, yPos, { align: 'right' })
    yPos += 10
  }

  yPos += 20

  // Payment History Section (Minimalist Version)
  if (invoice.payments && invoice.payments.length > 0) {
    doc.setTextColor(...accentColor)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('Payment History', margin, yPos)
    yPos += 8

    const paymentHeaders = [['DATE', 'AMOUNT', 'BALANCE']]
    let runningBalance = invoice.total

    const sortedPayments = [...invoice.payments].sort((a, b) => {
      const dateA = a.date && (a.date.toDate ? a.date.toDate() : new Date(a.date))
      const dateB = b.date && (b.date.toDate ? b.date.toDate() : new Date(b.date))
      return dateB - dateA
    })

    const paymentData = sortedPayments.map(payment => {
      const dateVal = payment.date && (payment.date.toDate ? payment.date.toDate() : new Date(payment.date))
      runningBalance -= payment.amount
      return [
        formatDate(dateVal),
        `$${formatCurrency(payment.amount)}`,
        `$${formatCurrency(runningBalance)}`
      ]
    })

    doc.autoTable({
      startY: yPos,
      head: paymentHeaders,
      body: paymentData,
      theme: 'plain',
      styles: {
        fontSize: 9,
        cellPadding: { top: 4, bottom: 4, left: 0, right: 0 },
        textColor: textColor,
        font: 'helvetica'
      },
      headStyles: {
        fillColor: false,
        textColor: grayColor,
        fontStyle: 'bold',
        fontSize: 8,
        lineWidth: { bottom: 0.5 },
        lineColor: separatorColor
      },
      bodyStyles: {
        lineWidth: { bottom: 0.5 },
        lineColor: [248, 250, 252]
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left' },
        1: { cellWidth: 40, halign: 'right' },
        2: { cellWidth: 40, halign: 'right' }
      },
      margin: { left: margin, right: margin }
    })

    yPos = doc.lastAutoTable.finalY + 20
  }

  // Notes & Terms Section (Modern clean format)
  if (invoice.notes || invoice.terms) {
    const spaceLeft = doc.internal.pageSize.getHeight() - yPos - margin
    // Avoid awkward page breaks if content is too long
    if (spaceLeft < 40) {
      doc.addPage()
      yPos = margin + 10
    }

    const maxTextWidth = contentWidth * 0.75 // Limit line length for better readability

    if (invoice.notes) {
      doc.setTextColor(...accentColor)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.text('NOTES', margin, yPos)
      yPos += 6

      doc.setTextColor(...grayColor)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')

      const notesLines = doc.splitTextToSize(invoice.notes, maxTextWidth)
      doc.text(notesLines, margin, yPos)
      yPos += (notesLines.length * 5) + 8
    }

    if (invoice.terms) {
      if (invoice.notes) yPos += 4 // Add spacing if notes existed
      
      doc.setTextColor(...accentColor)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.text('TERMS & CONDITIONS', margin, yPos)
      yPos += 6

      doc.setTextColor(...grayColor)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')

      const termsLines = doc.splitTextToSize(invoice.terms, maxTextWidth)
      doc.text(termsLines, margin, yPos)
    }
  }

  // Footer / Branding
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Subtle bottom line
  doc.setDrawColor(...separatorColor)
  doc.setLineWidth(0.5)
  doc.line(margin, pageHeight - margin + 2, pageWidth - margin, pageHeight - margin + 2)

  doc.setTextColor(...grayColor)
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  const businessEmail = settings.businessEmail ? ` | ${settings.businessEmail}` : ''
  const generatedText = `Generated seamlessly via nCinq Invoice${businessEmail}`
  doc.text(generatedText, pageWidth / 2, pageHeight - 10, { align: 'center' })

  return doc
}

// Generate invoice receipt PDF
export const generateReceiptPDF = async (invoice, client, settings, paymentAmount) => {
  const doc = new jsPDF()

  const primaryColor = [34, 197, 94] // Green for receipts
  const textColor = [30, 41, 59]
  const grayColor = [100, 116, 139]

  const margin = 20
  const pageWidth = doc.internal.pageSize.getWidth()

  // Header
  doc.setFillColor(...primaryColor)
  doc.rect(0, 0, pageWidth, 40, 'F')

  doc.setTextColor(255, 255, 255)
  doc.setFontSize(24)
  doc.setFont('helvetica', 'bold')
  doc.text('PAYMENT RECEIPT', pageWidth / 2, 25, { align: 'center' })

  let yPos = 55

  // Receipt Details
  doc.setTextColor(...textColor)
  doc.setFontSize(10)
  doc.text(`Receipt #: RCP-${Date.now().toString().slice(-6)}`, margin, yPos)
  doc.text(`Date: ${formatDate(new Date().toISOString())}`, pageWidth - margin, yPos, { align: 'right' })

  yPos += 15

  // Load Logo
  let logoData = null
  try {
    if (settings.logo) {
      logoData = await imageToBase64(settings.logo)
    } else {
      logoData = await imageToBase64('/ncinq-favicon.png')
    }
  } catch (e) {
    console.log('Receipt logo not found')
  }

  // Draw Logo
  if (logoData && logoData.dataUrl) {
    const maxWidth = 30
    const maxHeight = 15
    const ratio = Math.min(maxWidth / logoData.width, maxHeight / logoData.height)
    const logoWidth = logoData.width * ratio
    const logoHeight = logoData.height * ratio

    doc.addImage(logoData.dataUrl, 'PNG', margin, yPos, logoWidth, logoHeight)
    yPos += logoHeight + 5
  }

  // Business Info
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(settings.businessName || 'Business Name', margin, yPos)
  yPos += 5

  if (settings.businessAddress) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...grayColor)
    const addressLines = doc.splitTextToSize(settings.businessAddress, pageWidth / 2)
    doc.text(addressLines, margin, yPos)
    doc.text(settings.businessEmail || '', margin, yPos + (addressLines.length * 4))
  }

  yPos += 35

  // Payment Info Box
  doc.setFillColor(248, 250, 252)
  doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 35, 3, 3, 'F')

  doc.setFontSize(9)
  doc.setTextColor(...grayColor)
  doc.text('Payment For Invoice', margin + 10, yPos + 10)
  doc.text('Amount Paid', margin + 10, yPos + 22)

  doc.setTextColor(...textColor)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text(invoice.invoiceNumber, margin + 10, yPos + 16)
  doc.setFontSize(14)
  doc.setTextColor(...primaryColor)
  doc.text(`$${formatCurrency(paymentAmount)}`, margin + 10, yPos + 28)

  // Currency Exchange Equivalent (Receipt)
  if (settings.enableExchange && settings.exchangeRate > 0) {
    doc.setFontSize(8)
    doc.setTextColor(...grayColor)
    doc.setFont('helvetica', 'italic')
    const convertedAmount = paymentAmount * settings.exchangeRate
    const formattedConverted = convertedAmount.toLocaleString('en-US', {
      style: 'currency',
      currency: settings.exchangeCurrency || 'GYD'
    })
    doc.text(`Equivalent: ${formattedConverted}`, margin + 10, yPos + 34)
  }

  // Remaining Balance if partial
  if (invoice.status === 'partial') {
    const balanceDue = invoice.total - (invoice.partialAmount || 0)
    doc.setFontSize(9)
    doc.setTextColor(...grayColor)
    doc.setFont('helvetica', 'normal')
    doc.text('Remaining Balance', pageWidth - margin - 40, yPos + 22)
    doc.setTextColor(239, 68, 68) // Red
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text(`$${formatCurrency(balanceDue)}`, pageWidth - margin - 10, yPos + 28, { align: 'right' })
  }

  yPos += 50

  // Client Info
  doc.setFontSize(9)
  doc.setTextColor(...grayColor)
  doc.text('Received From', margin, yPos)
  yPos += 6
  doc.setTextColor(...textColor)
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  if (client?.businessName) {
    doc.text(client.businessName, margin, yPos)
    yPos += 6
    doc.setFont('helvetica', 'normal')
    doc.text(client?.name || '', margin, yPos)
  } else {
    doc.text(client?.name || 'Client Name', margin, yPos)
  }
  if (client?.address) {
    const addressLines = doc.splitTextToSize(client.address, pageWidth / 2)
    doc.text(addressLines, margin, yPos)
    yPos += addressLines.length * 4
  }

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(...grayColor)
  doc.text(client?.email || '', margin, yPos)

  yPos += 20

  // Payment Status
  doc.setFillColor(34, 197, 94)
  doc.roundedRect(margin, yPos, 50, 12, 2, 2, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.text('PAID', margin + 25, yPos + 8, { align: 'center' })

  // Footer
  doc.setTextColor(...grayColor)
  doc.setFontSize(8)
  doc.text(`Thank you for your payment!`, pageWidth / 2, doc.internal.pageSize.getHeight() - 15, { align: 'center' })

  return doc
}

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Download Receipt PDF
export const downloadReceiptPDF = async (invoice, client, settings, paymentAmount) => {
  const doc = await generateReceiptPDF(invoice, client, settings, paymentAmount)
  doc.save(`Receipt-${invoice.invoiceNumber}.pdf`)
}

// Download PDF
export const downloadInvoicePDF = async (invoice, client, settings) => {
  const doc = await generateInvoicePDF(invoice, client, settings)
  // Include subject in filename if available
  const filename = invoice.subject
    ? `${invoice.subject.replace(/[^a-z0-9]/gi, '_')}-${invoice.invoiceNumber}.pdf`
    : `${invoice.invoiceNumber}.pdf`
  doc.save(filename)
}

// Open PDF in new tab
export const openInvoicePDF = async (invoice, client, settings) => {
  const doc = await generateInvoicePDF(invoice, client, settings)
  const pdfBlob = doc.output('blob')
  const pdfUrl = URL.createObjectURL(pdfBlob)
  window.open(pdfUrl, '_blank')
}

// Print PDF
export const printInvoicePDF = async (invoice, client, settings) => {
  const doc = await generateInvoicePDF(invoice, client, settings)
  doc.autoPrint()
  doc.output('dataurlnewwindow')
}
